import { useAuth, API } from "@/App";
import { familyShareMessage, depShareMessage, createDepLink } from "@/lib/gigShare";
import { openExternalLink } from "@/lib/utils";
import { isStandalonePwa } from "@/lib/utils";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Music2, Calendar, Plus, Download, Sparkles, LogOut,
  Clock, MapPin, Search, Users, Settings, Share2, Copy, MessageCircle, Check,
  Home, BarChart3, MoreHorizontal, ListMusic, Building2, Receipt, X, Navigation, Smartphone, Eye, EyeOff, GripVertical, Loader2, Plane, Undo2, Send
} from "lucide-react";
import { DragListenersContext, useDragListeners, DragHandle } from "@/hooks/useDragHandle";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, rectSortingStrategy } from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import NotificationBell from "@/components/NotificationBell";
import TestEnvironmentBanner from "@/components/TestEnvironmentBanner";
import { format, parseISO, isAfter } from "date-fns";
import { GigBrief } from "@/components/GigBrief";
import { GigCountdown } from "@/components/GigCountdown";
import { WellbeingPrompts } from "@/components/WellbeingPrompts";
import { WeeklySocialSchedule } from "@/components/WeeklySocialSchedule";
import { UpcomingGigs } from "@/components/UpcomingGigs";
import { RevenueOverview } from "@/components/RevenueOverview";
import { Kicker } from "@/design-system/primitives";
import { tokens } from "@/design-system/tokens";
import { TrialBanner, PlanBadge, UpgradeModal } from "@/components/Subscription";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";
import { useCanInstall } from "@/components/MobileInstallBanner";
import { QRCodeSVG } from "qrcode.react";
import { ThemeToggle } from "@/theme/ThemeProvider";

// Share Popover for Dashboard gig cards
const SharePopover = ({ shareToken, gigId, gig }) => {
  const [copied, setCopied] = useState(false);
  const apiBase = API.startsWith('http') ? API : window.location.origin + API;
  const shareUrl = `${apiBase}/share/gig/${shareToken}/og`;
  const msg = encodeURIComponent(familyShareMessage(gig || {}, shareUrl));

  const sendDepPack = async () => {
    try {
      const depUrl = await createDepLink(apiClient, gigId);
      await copyToClipboard(depUrl);
      toast.success("Deputy link copied!");
      openExternalLink(`https://wa.me/?text=${encodeURIComponent(depShareMessage(gig || {}, depUrl))}`);
    } catch {
      toast.error("Could not create the deputy link");
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className="p-1.5 rounded-md hover:bg-secondary/80 text-muted-foreground hover:text-primary transition-colors"
          data-testid={`share-link-${gigId}`}
        >
          <Share2 className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-2" align="end" onClick={(e) => e.stopPropagation()}>
        <div className="space-y-1">
          <button
            className="w-full flex items-center gap-2 text-sm px-2 py-1.5 rounded-md hover:bg-secondary transition-colors"
            data-testid={`share-copy-${gigId}`}
            onClick={() => {
              copyToClipboard(shareUrl);
              setCopied(true);
              toast.success("Link copied!");
              setTimeout(() => setCopied(false), 2000);
            }}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied!" : "Copy link"}
          </button>
          <button
            className="w-full flex items-center gap-2 text-sm px-2 py-1.5 rounded-md hover:bg-secondary transition-colors"
            data-testid={`dep-this-out-${gigId}`}
            onClick={(e) => { e.stopPropagation(); sendDepPack(); }}
          >
            <MessageCircle className="h-3.5 w-3.5" />
            Dep this out
          </button>
          <a
            href={`https://wa.me/?text=${msg}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center gap-2 text-sm px-2 py-1.5 rounded-md hover:bg-secondary transition-colors"
            data-testid={`share-whatsapp-${gigId}`}
            onClick={(e) => {
              e.stopPropagation();
              if (isStandalonePwa()) {
                e.preventDefault();
                window.location.href = e.currentTarget.href;
              }
            }}
          >
            <MessageCircle className="h-3.5 w-3.5" />
            WhatsApp
          </a>
          <a
            href={`sms:?body=${msg}`}
            className="w-full flex items-center gap-2 text-sm px-2 py-1.5 rounded-md hover:bg-secondary transition-colors"
            data-testid={`share-sms-${gigId}`}
            onClick={(e) => e.stopPropagation()}
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            Text
          </a>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Share Public Profile (QR + link) — header widget. Lazily auto-claims a
// username via the idempotent backend endpoint the first time it's opened,
// so any musician can grab a shareable profile link with zero setup.
const ShareProfilePopover = () => {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState(user?.username || "");
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const profileUrl = username ? `${window.location.origin}/musician/${username}` : "";
  const shareMsg = encodeURIComponent(`Check out my gigs on Fretless: ${profileUrl}`);

  const handleOpenChange = async (next) => {
    setOpen(next);
    if (next && !username) {
      setGenerating(true);
      try {
        const res = await apiClient.post("/user/username/auto-generate");
        const u = res.data?.username || "";
        setUsername(u);
        if (u && setUser) setUser((prev) => (prev ? { ...prev, username: u } : prev));
      } catch {
        // leave username empty — UI falls back to a Settings link
      } finally {
        setGenerating(false);
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2" title="Share your public profile" data-testid="share-profile-btn">
          <Share2 className="h-4 w-4" />
          <span className="hidden sm:inline text-sm">Share</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-4" align="end">
        {generating ? (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Preparing your link…
          </div>
        ) : username ? (
          <div className="flex flex-col items-center gap-3">
            <div className="self-start">
              <h3 className="font-semibold text-sm">Share your profile</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Scan or send this so other musicians can see your gigs — and join Fretless.
              </p>
            </div>
            <div className="bg-white p-3 rounded-xl" data-testid="share-profile-qr">
              <QRCodeSVG value={profileUrl} size={150} fgColor="#0D0D0D" bgColor="#FFFFFF" level="M" />
            </div>
            <div className="w-full space-y-1">
              <button
                className="w-full flex items-center gap-2 text-sm px-2 py-1.5 rounded-md hover:bg-secondary transition-colors"
                data-testid="share-profile-copy"
                onClick={() => {
                  copyToClipboard(profileUrl);
                  setCopied(true);
                  toast.success("Profile link copied!");
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Copied!" : "Copy link"}
              </button>
              <a
                href={`https://wa.me/?text=${shareMsg}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center gap-2 text-sm px-2 py-1.5 rounded-md hover:bg-secondary transition-colors"
                onClick={(e) => {
                  if (isStandalonePwa()) {
                    e.preventDefault();
                    window.location.href = e.currentTarget.href;
                  }
                }}
              >
                <MessageCircle className="h-3.5 w-3.5" />
                WhatsApp
              </a>
              <a
                href={`sms:?body=${shareMsg}`}
                className="w-full flex items-center gap-2 text-sm px-2 py-1.5 rounded-md hover:bg-secondary transition-colors"
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                Text
              </a>
            </div>
            <p className="text-[11px] text-muted-foreground text-center break-all w-full">
              {profileUrl.replace(/^https?:\/\//, "")}
            </p>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground py-6 text-center">
            Couldn't prepare your link. Set a username in{" "}
            <Link to="/settings?tab=profile" className="text-primary hover:underline">Settings</Link>.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

// Sortable Grid Item Wrapper - properly integrates with dnd-kit
const SortableGridItem = ({ id, children, fullWidth = false }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <DragListenersContext.Provider value={{ attributes, listeners }}>
      <div
        ref={setNodeRef}
        style={style}
        className={`h-full ${fullWidth ? 'md:col-span-2' : ''} ${isDragging ? 'z-50' : ''} group`}
      >
        {children}
      </div>
    </DragListenersContext.Provider>
  );
};

// Layout Component
export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const canInstall = useCanInstall();
  const [showIOSInstall, setShowIOSInstall] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const path = location.pathname;

  // Bottom nav items (mobile)
  const bottomTabs = [
    { to: "/dashboard", icon: Home, label: "Home" },
    { to: "/gigs", icon: Music2, label: "Gigs" },
    { to: "/calendar", icon: Calendar, label: "Calendar" },
    { to: "/social", icon: Sparkles, label: "Social" },
  ];

  const moreItems = [
    { to: "/setlists", icon: ListMusic, label: "Setlists" },
    { to: "/scene", icon: Building2, label: "Scene" },
    { to: "/money", icon: Receipt, label: "Money" },
    { to: "/stats", icon: BarChart3, label: "Stats" },
    { to: "/settings", icon: Settings, label: "Settings" },
  ];

  const isActive = (to) => {
    if (to === "/dashboard") return path === "/dashboard";
    return path.startsWith(to);
  };

  const isMoreActive = moreItems.some((m) => isActive(m.to));

  // Token-driven nav link. Uses tokens.type.navLink for the paired
  // font/size/weight bundle; defaults to color.textDim, brand orange
  // when the current route matches, color.text on hover. Closure-scoped
  // so it can read isActive() without prop-drilling.
  const NavLink = ({ to, children, "data-testid": testId }) => {
    const active = isActive(to);
    return (
      <Link
        to={to}
        data-testid={testId}
        style={{
          ...tokens.type.navLink,
          color: active ? tokens.color.orange : tokens.color.textDim,
          textDecoration: "none",
          transition: `color ${tokens.motion.fast}`,
          whiteSpace: "nowrap",
        }}
        onMouseEnter={(e) => {
          if (!active) e.currentTarget.style.color = tokens.color.text;
        }}
        onMouseLeave={(e) => {
          if (!active) e.currentTarget.style.color = tokens.color.textDim;
        }}
      >
        {children}
      </Link>
    );
  };

  return (
    // 100dvh = dynamic viewport height — accounts for Chrome iOS address/tab bar resize
    <div className="min-h-[100dvh] bg-background">
      <TestEnvironmentBanner />
      {/* Desktop Navigation — DS-token nav links. Sticky / glass-nav /
          z-index / safe-area-inset stay as Tailwind+inline because they're
          structural, not design-token concerns. */}
      <nav
        className="hidden md:block glass-nav sticky top-0 z-50"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top) + 1rem)',
          paddingBottom: tokens.space.lg,
          paddingLeft: tokens.space.xl,
          paddingRight: tokens.space.xl,
        }}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/dashboard" style={{ display: "flex", alignItems: "center", gap: tokens.space.sm, textDecoration: "none" }}>
            <img src="/icons/quaver.png" alt="Fretless" className="h-7 w-auto" />
            <span style={{ ...tokens.type.wordmark }}>
              <span style={{ color: tokens.color.text }}>Fret</span>
              <span style={{ color: tokens.color.orange }}>less</span>
            </span>
          </Link>

          <div style={{ display: "flex", alignItems: "center", gap: tokens.space.xl }}>
            <NavLink to="/dashboard" data-testid="nav-dashboard">Dashboard</NavLink>
            <NavLink to="/gigs"      data-testid="nav-gigs">Gigs</NavLink>
            <NavLink to="/calendar"  data-testid="nav-calendar">Calendar</NavLink>
            <NavLink to="/setlists"  data-testid="nav-setlists">Setlists</NavLink>
            <NavLink to="/scene"     data-testid="nav-scene">Scene</NavLink>
            <NavLink to="/money"     data-testid="nav-money">Money</NavLink>
            <NavLink to="/social"    data-testid="nav-social">Social</NavLink>
            <NavLink to="/stats"     data-testid="nav-stats">Stats</NavLink>
            <NavLink to="/settings"  data-testid="nav-settings">Settings</NavLink>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: tokens.space.lg }}>
            {user && <NotificationBell />}
            {user && (
              <div style={{ display: "flex", alignItems: "center", gap: tokens.space.md }}>
                <img
                  src={user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=f59e0b&color=000`}
                  alt={user.name}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: tokens.radius.pill,
                    border: tokens.border.card,
                  }}
                />
                <span
                  style={{
                    ...tokens.type.navLink,
                    color: tokens.color.text,
                  }}
                >
                  {user.name}
                </span>
                <PlanBadge subscription={user?.subscription} />
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Mobile Top Bar — minimal logo + avatar */}
      <div
        className="md:hidden glass-nav sticky top-0 z-50 flex items-center justify-between"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)',
          paddingBottom: tokens.space.md,
          paddingLeft: tokens.space.md,
          paddingRight: tokens.space.md,
        }}
      >
        <Link to="/dashboard" style={{ display: "flex", alignItems: "center", gap: tokens.space.sm, textDecoration: "none" }}>
          <img src="/icons/quaver.png" alt="Fretless" className="h-6 w-auto" />
          <span style={{ ...tokens.type.wordmark }}>
            <span style={{ color: tokens.color.text }}>Fret</span>
            <span style={{ color: tokens.color.orange }}>less</span>
          </span>
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: tokens.space.sm }}>
          {user && <NotificationBell />}
          {user && (
            <img
              src={user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=f59e0b&color=000&size=32`}
              alt={user.name}
              style={{
                width: 28,
                height: 28,
                borderRadius: tokens.radius.pill,
                border: tokens.border.card,
              }}
            />
          )}
          <PlanBadge subscription={user?.subscription} />
        </div>
      </div>

      {/* Main Content */}
      {/*
        Mobile: calc() gives the sum of the home indicator inset + fixed nav bar height (60px).
        On Chrome iOS the address bar is dynamic so we can't use a fixed Tailwind pb-28;
        env(safe-area-inset-bottom) resolves to 0px on non-notch devices and ~34px on iPhone X+.
        Desktop: md:pb-8 (32px) is enough since the bottom nav is hidden at md+.
      */}
      <main
        className="max-w-7xl mx-auto px-3 md:px-6 py-6 md:py-8"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 60px)' }}
      >
        {children}
      </main>

      {/* Mobile Bottom Nav */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-[9999] bg-card border-t border-border"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        data-testid="mobile-bottom-nav"
      >
        <div className="flex items-center justify-around px-2 pt-2 pb-1">
          {bottomTabs.map((tab) => {
            const active = isActive(tab.to);
            return (
              <Link
                key={tab.to}
                to={tab.to}
                className={`flex flex-col items-center gap-0.5 py-1 px-3 min-w-[56px] transition-colors ${active ? "text-primary" : "text-muted-foreground"}`}
                data-testid={`mobile-nav-${tab.label.toLowerCase()}`}
              >
                <tab.icon className="h-5 w-5" />
                <span className="text-[10px] font-medium">{tab.label}</span>
              </Link>
            );
          })}
          {/* More button */}
          <button
            onClick={() => setMoreOpen(true)}
            className={`flex flex-col items-center gap-0.5 py-1 px-3 min-w-[56px] transition-colors ${isMoreActive ? "text-primary" : "text-muted-foreground"}`}
            data-testid="mobile-nav-more"
          >
            <MoreHorizontal className="h-5 w-5" />
            <span className="text-[10px] font-medium">More</span>
          </button>
        </div>
      </nav>

      {/* More Drawer (mobile) — z above the bottom nav's 9999 so the drawer
          (opened from that nav) and its lower items sit over it, not behind. */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-[10000]" data-testid="more-drawer">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMoreOpen(false)} />
          {/* Drawer */}
          <div
            className="absolute bottom-0 left-0 right-0 bg-card rounded-t-2xl border-t border-border animate-slide-up"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <span className="text-sm font-semibold text-foreground">More</span>
              <button onClick={() => setMoreOpen(false)} className="text-muted-foreground hover:text-foreground" aria-label="Close menu">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-3 pb-4">
              {moreItems.map((item) => {
                const active = isActive(item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMoreOpen(false)}
                    className={`flex items-center gap-4 px-4 py-3.5 rounded-xl transition-colors ${active ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted"}`}
                    data-testid={`more-nav-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </Link>
                );
              })}
              {/* Install app (mobile only, when not already installed) */}
              {canInstall && (
                <button
                  onClick={async () => {
                    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
                    if (isIOS) {
                      setMoreOpen(false);
                      setShowIOSInstall(true);
                    } else if (deferredPrompt) {
                      deferredPrompt.prompt();
                      await deferredPrompt.userChoice;
                      setDeferredPrompt(null);
                      setMoreOpen(false);
                    }
                  }}
                  className="flex items-center gap-4 px-4 py-3.5 rounded-xl text-primary hover:bg-primary/10 w-full"
                  data-testid="more-nav-install"
                >
                  <Smartphone className="h-5 w-5" />
                  <span className="text-sm font-medium">Install app</span>
                </button>
              )}
              {/* Sign out */}
              <button
                onClick={() => { setMoreOpen(false); handleLogout(); }}
                className="flex items-center gap-4 px-4 py-3.5 rounded-xl text-destructive hover:bg-muted w-full mt-1"
                data-testid="more-nav-signout"
              >
                <LogOut className="h-5 w-5" />
                <span className="text-sm font-medium">Sign out</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* iOS install overlay triggered from More drawer */}
      {showIOSInstall && (
        <div className="md:hidden fixed inset-0 z-[200] bg-black/90 backdrop-blur-md flex items-end justify-center animate-fade-in" data-testid="ios-install-drawer">
          <div className="w-full max-w-md bg-card rounded-t-2xl p-6 relative" style={{ paddingBottom: "env(safe-area-inset-bottom, 16px)" }}>
            <button onClick={() => setShowIOSInstall(false)} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground" aria-label="Close"><X className="h-5 w-5" /></button>
            <h2 className="text-xl font-bold text-foreground mb-1">Add Fretless to Home Screen</h2>
            <p className="text-muted-foreground text-sm mb-5">Use it like an app — no app store needed</p>
            <div className="divide-y divide-border text-sm text-foreground">
              <div className="flex items-center gap-4 py-3"><div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">1</div><span>Tap the <strong className="text-primary">Share</strong> icon at the bottom of Safari</span></div>
              <div className="flex items-center gap-4 py-3"><div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">2</div><span>Scroll and tap <strong className="text-primary">"Add to Home Screen"</strong></span></div>
              <div className="flex items-center gap-4 py-3"><div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">3</div><span>Tap <strong className="text-primary">"Add"</strong> in the top right</span></div>
            </div>
            <button onClick={() => setShowIOSInstall(false)} className="w-full mt-5 py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm" data-testid="ios-install-done">Got it</button>
          </div>
        </div>
      )}
    </div>
  );
};

// Module-level accounting-status cache. GigCard renders many times per page —
// without this, every card would issue its own pair of status fetches. The
// promise dedupes concurrent first-callers; the cache satisfies subsequent ones.
let _accountingStatusCache = null;
let _accountingStatusPromise = null;
const getAccountingStatus = async () => {
  if (_accountingStatusCache) return _accountingStatusCache;
  if (_accountingStatusPromise) return _accountingStatusPromise;
  _accountingStatusPromise = Promise.all([
    apiClient.get("/accounting/freeagent/status").catch(() => ({ data: { connected: false } })),
    apiClient.get("/accounting/xero/status").catch(() => ({ data: { connected: false } })),
    apiClient.get("/accounting/quickbooks/status").catch(() => ({ data: { connected: false } })),
  ]).then(([fa, xero, qbo]) => {
    _accountingStatusCache = {
      freeagent: !!fa.data?.connected,
      xero: !!xero.data?.connected,
      quickbooks: !!qbo.data?.connected,
    };
    return _accountingStatusCache;
  });
  return _accountingStatusPromise;
};

// Gig Card Component — token-driven tile, used on both /dashboard
// (Upcoming Gigs widget — actually that uses ListRow; this is the
// /gigs grid tile) and /gigs.
//
// Optional `selectable`/`selected`/`onSelectToggle` props enable a small
// checkbox overlay used by GigManager's Select-mode (Add to Invoice /
// Create Template / Delete). Other consumers (Dashboard widgets) leave
// these undefined and see no checkbox.
export const GigCard = ({ gig, onEdit, onDuplicate, highlighted = false, selectable = false, selected = false, onSelectToggle, accStatus: accStatusOverride }) => {
  const navigate = useNavigate();
  const tk = tokens;
  // Accounting integration buttons (FA / Xero one-click push). Status comes from
  // the module-level cache so we don't fetch per-card; pushingPlatform locks the
  // button during the create+push round-trip. The dev harness can pass an
  // accStatus override to render the FA/Xero buttons without a live connection.
  const [fetchedAccStatus, setAccStatus] = useState({ freeagent: false, xero: false, quickbooks: false });
  const accStatus = accStatusOverride || fetchedAccStatus;
  const [pushingPlatform, setPushingPlatform] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(false);
  useEffect(() => { if (!accStatusOverride) getAccountingStatus().then(setAccStatus).catch(() => {}); }, [accStatusOverride]);

  const handlePushFromGig = async (platform) => {
    // Vital info guards — fail fast with a clear toast rather than creating
    // a broken invoice and discovering the gap mid-push.
    if (!gig.fee || gig.fee <= 0) {
      toast.error("Add a fee to this gig first");
      return;
    }
    if (!gig.venue_name && !gig.band_id) {
      toast.error("Add a venue or band to this gig first");
      return;
    }
    setPushingPlatform(platform);
    try {
      // Resolve recipient via the backend (honours any per-gig invoice_recipient override).
      let recipientName = gig.venue_name || "";
      let recipientEmail = "";
      try {
        const r = (await apiClient.get(`/gigs/${gig.gig_id}/invoice-recipient`)).data;
        recipientName = r.name || recipientName;
        recipientEmail = r.email || "";
      } catch {}

      let gigDateStr = "";
      try { gigDateStr = format(parseISO(gig.date), "dd MMM yyyy"); } catch { gigDateStr = gig.date || ""; }
      const line_items = [{
        description: `Performance fee - ${gig.artist_name || ""} at ${gig.venue_name || ""}, ${gigDateStr}`,
        quantity: 1,
        unit_price: gig.fee,
        amount: gig.fee,
      }];
      if (gig.add_per_diems_to_invoice && gig.per_diems) {
        const pdAmount = (gig.per_diems.breakfast || 0) + (gig.per_diems.lunch || 0) + (gig.per_diems.dinner || 0);
        if (pdAmount > 0) {
          line_items.push({ description: "Per Diems", quantity: 1, unit_price: pdAmount, amount: pdAmount });
        }
      }
      if (gig.add_mileage_to_invoice && gig.mileage_cost_for_invoice && gig.mileage_cost_for_invoice > 0) {
        // Show the actual miles + rate (e.g. "27 mi @ £0.45/mi") rather than
        // a flat lump sum, so the recipient can see how the figure was reached.
        const miles = gig.mileage_miles_for_invoice;
        const rate = miles > 0 ? gig.mileage_cost_for_invoice / miles : null;
        const description = miles > 0 && rate
          ? `Mileage: ${miles} mi @ £${rate.toFixed(2)}/mi`
          : "Mileage Reimbursement";
        line_items.push({ description, quantity: 1, unit_price: gig.mileage_cost_for_invoice, amount: gig.mileage_cost_for_invoice });
      }
      if (gig.expenses && gig.expenses > 0) {
        line_items.push({ description: "Travel & expenses", quantity: 1, unit_price: gig.expenses, amount: gig.expenses });
      }

      // 1. Create Fretless invoice
      const createRes = await apiClient.post("/invoices", {
        gig_id: gig.gig_id,
        recipient_type: "venue",
        recipient_name: recipientName,
        recipient_email: recipientEmail || null,
        recipient_address: null,
        line_items,
        vat_rate: 0,
        currency: "GBP",
      });
      const invoiceId = createRes.data?.invoice_id;
      if (!invoiceId) throw new Error("Invoice creation returned no ID");

      // 2. Push to accounting platform
      const pushRes = await apiClient.post(`/invoices/${invoiceId}/push-to-${platform}`);
      const url = pushRes.data?.freeagent_invoice_url || pushRes.data?.xero_invoice_url || pushRes.data?.quickbooks_invoice_url;
      const label = platform === "freeagent" ? "FreeAgent" : platform === "quickbooks" ? "QuickBooks" : "Xero";
      toast.success(`Invoice created and pushed to ${label}`, {
        action: url ? { label: "Open", onClick: () => window.open(url, "_blank") } : undefined,
      });
      // Notify parent lists (Dashboard / GigManager) so they can refetch and
      // the gig's fee_status flips from "pending" → "invoiced" without a reload.
      window.dispatchEvent(new CustomEvent("fretless:gig-invoiced", { detail: { gigId: gig.gig_id } }));
    } catch (e) {
      const msg = e.response?.data?.detail || `Failed to push to ${platform === "freeagent" ? "FreeAgent" : platform === "quickbooks" ? "QuickBooks" : "Xero"}`;
      toast.error(msg, {
        action: { label: "Open in Fretless", onClick: () => navigate(`/invoices/new?gig=${gig.gig_id}`) },
      });
    } finally {
      setPushingPlatform(null);
    }
  };

  // Status pill: map shadcn .status-* classes to DS-token visuals.
  const statusVisual = (() => {
    const s = gig.status || "confirmed";
    if (s === "cancelled") return { bg: "rgba(239, 68, 68, 0.12)", color: tk.color.red };
    if (s === "pending")   return { bg: tk.color.amberSoft,        color: tk.color.amber };
    return                        { bg: "rgba(34, 193, 151, 0.12)", color: tk.color.green };
  })();

  // Payment status pill — same visual language as statusVisual above.
  // Pending/Invoiced tap to mark paid; Paid taps again to undo back to
  // pending. The backend keeps any linked invoice (and its other bundled
  // gigs) in sync with whichever direction this flips.
  const paymentVisual = (() => {
    const s = gig.fee_status || "pending";
    if (s === "paid")     return { bg: "rgba(34, 193, 151, 0.12)", color: tk.color.green,  label: "Paid" };
    if (s === "invoiced") return { bg: "rgba(122, 90, 240, 0.12)", color: tk.color.purple, label: "Invoiced" };
    // Pending only means something once the gig has actually happened —
    // a future gig with a fee set hasn't earned anything yet, so the
    // pill stays hidden (see render guard below) until then.
    const todayStr = format(new Date(), "yyyy-MM-dd");
    const isFuture = gig.date && gig.date > todayStr;
    return { bg: tk.color.amberSoft, color: tk.color.amber, label: isFuture ? null : "Pending" };
  })();

  const handleTogglePaid = async (e) => {
    e.stopPropagation();
    if (markingPaid) return;
    const targetStatus = paymentVisual.label === "Paid" ? "pending" : "paid";
    setMarkingPaid(true);
    try {
      await apiClient.put(`/gigs/${gig.gig_id}`, { fee_status: targetStatus });
      toast.success(targetStatus === "paid" ? "Marked as paid" : "Marked as pending");
      window.dispatchEvent(new CustomEvent("fretless:gig-invoiced", { detail: { gigId: gig.gig_id } }));
    } catch {
      toast.error("Failed to update payment status");
    } finally {
      setMarkingPaid(false);
    }
  };

  const formattedDate = gig.date
    ? format(parseISO(gig.date), 'EEE, MMM d, yyyy')
    : 'Date TBA';

  // Multi-day gigs show the trip's date range (travel_date → return_date)
  // instead of a separate departure/return-airport sub-block — "20th – 22nd
  // June" rather than itemised flight times. Falls back to gig.date at
  // either end if travel_date/return_date weren't set.
  const ordinal = (n) => {
    const v = n % 100;
    if (v >= 11 && v <= 13) return `${n}th`;
    switch (n % 10) {
      case 1: return `${n}st`;
      case 2: return `${n}nd`;
      case 3: return `${n}rd`;
      default: return `${n}th`;
    }
  };
  const tripDateRange = (() => {
    if (!gig.is_multiday) return null;
    const startStr = gig.travel_date || gig.date;
    const endStr = gig.return_date || gig.date;
    if (!startStr || !endStr) return null;
    try {
      const start = parseISO(startStr);
      const end = parseISO(endStr);
      const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
      return sameMonth
        ? `${ordinal(start.getDate())} – ${ordinal(end.getDate())} ${format(end, "MMMM")}`
        : `${format(start, "d MMM")} – ${format(end, "d MMM")}`;
    } catch {
      return null;
    }
  })();

  // Footer invoice button(s) — short "Invoice" label (colour-coded per platform;
  // full action in title/aria) so all four footer items — fee · status ·
  // payment · invoice — fit on one evenly-spaced row even on a narrow card.
  const showFA = accStatus.freeagent && gig.fee && gig.fee_status === "pending";
  const showXero = accStatus.xero && gig.fee && gig.fee_status === "pending";
  const showQBO = accStatus.quickbooks && gig.fee && gig.fee_status === "pending";
  const showPaymentPill = gig.fee && paymentVisual.label;
  // Footer items: fee · status · payment · invoice(s). With 3 the fee stays
  // locked to its text-aligned indent (selectable → 40px). With 4+ the fee
  // unlocks and the row uses the FULL card width so everything fits one
  // evenly-spaced line on a narrow card.
  const footerCount = (gig.fee ? 1 : 0) + 1 + (showPaymentPill ? 1 : 0) + (showFA ? 1 : 0) + (showXero ? 1 : 0) + (showQBO ? 1 : 0);
  const footerPadLeft = footerCount >= 4 ? 0 : (selectable ? 40 : 0);
  const freeAgentBtn = showFA ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); handlePushFromGig("freeagent"); }}
      disabled={pushingPlatform !== null}
      data-testid={`push-freeagent-gig-${gig.gig_id}`}
      title="Create invoice + push to FreeAgent"
      aria-label="Create invoice + push to FreeAgent"
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 11px", borderRadius: tk.radius.pill,
        background: "rgba(16, 185, 129, 0.1)", border: "none", cursor: "pointer",
        color: "rgb(52, 211, 153)",
        fontFamily: tk.font.mono, fontSize: 11, fontWeight: 700,
        letterSpacing: "0.04em", whiteSpace: "nowrap",
        transition: `all ${tk.motion.fast}`,
        opacity: pushingPlatform !== null && pushingPlatform !== "freeagent" ? 0.4 : 1,
      }}
    >
      {pushingPlatform === "freeagent"
        ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
        : "Invoice"}
    </button>
  ) : null;
  const xeroBtn = showXero ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); handlePushFromGig("xero"); }}
      disabled={pushingPlatform !== null}
      data-testid={`push-xero-gig-${gig.gig_id}`}
      title="Create invoice + push to Xero"
      aria-label="Create invoice + push to Xero"
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 11px", borderRadius: tk.radius.pill,
        background: "rgba(14, 165, 233, 0.1)", border: "none", cursor: "pointer",
        color: "rgb(56, 189, 248)",
        fontFamily: tk.font.mono, fontSize: 11, fontWeight: 700,
        letterSpacing: "0.04em", whiteSpace: "nowrap",
        transition: `all ${tk.motion.fast}`,
        opacity: pushingPlatform !== null && pushingPlatform !== "xero" ? 0.4 : 1,
      }}
    >
      {pushingPlatform === "xero"
        ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
        : "Invoice"}
    </button>
  ) : null;
  const quickbooksBtn = showQBO ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); handlePushFromGig("quickbooks"); }}
      disabled={pushingPlatform !== null}
      data-testid={`push-quickbooks-gig-${gig.gig_id}`}
      title="Create invoice + push to QuickBooks"
      aria-label="Create invoice + push to QuickBooks"
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 11px", borderRadius: tk.radius.pill,
        background: "rgba(34, 197, 94, 0.1)", border: "none", cursor: "pointer",
        color: "rgb(74, 222, 128)",
        fontFamily: tk.font.mono, fontSize: 11, fontWeight: 700,
        letterSpacing: "0.04em", whiteSpace: "nowrap",
        transition: `all ${tk.motion.fast}`,
        opacity: pushingPlatform !== null && pushingPlatform !== "quickbooks" ? 0.4 : 1,
      }}
    >
      {pushingPlatform === "quickbooks"
        ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
        : "Invoice"}
    </button>
  ) : null;

  return (
    <div
      id={`gig-card-${gig.gig_id}`}
      onClick={() => onEdit && onEdit(gig)}
      style={{
        background: tk.color.cardBg,
        border: selected
          ? `2px solid ${tk.color.orange}`
          : highlighted ? `2px solid ${tk.color.orange}` : tk.border.card,
        borderRadius: tk.radius.xl,
        overflow: "hidden",
        cursor: onEdit ? "pointer" : "default",
        transition: `border-color ${tk.motion.base}`,
      }}
      onMouseEnter={(e) => {
        if (!highlighted && !selected) e.currentTarget.style.borderColor = tk.color.orangeLine;
      }}
      onMouseLeave={(e) => {
        if (!highlighted && !selected) e.currentTarget.style.borderColor = tk.color.cardBorder;
      }}
    >
      {selected && (
        <div style={{ position: "absolute", inset: 0, background: tk.color.orangeSoft, pointerEvents: "none", zIndex: 1 }} />
      )}

      <div style={{ position: "relative", zIndex: 2, padding: tk.space.lg }}>
        {/* TOP: checkbox + text block (artist, venue+directions, date+time) */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: tk.space.sm, marginBottom: tk.space.lg }}>
          {selectable && (
            <button
              type="button"
              aria-pressed={selected}
              aria-label={selected ? "Deselect gig" : "Select gig"}
              onClick={(e) => { e.stopPropagation(); onSelectToggle && onSelectToggle(gig.gig_id); }}
              data-testid={`select-gig-row-${gig.gig_id}`}
              style={{
                flexShrink: 0,
                width: 40,
                height: 40,
                marginLeft: -8,
                marginTop: -8,
                display: "grid",
                placeItems: "center",
                background: "transparent",
                border: "none",
                borderRadius: tk.radius.md,
                cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              <span
                data-testid={`select-gig-${gig.gig_id}`}
                style={{
                  width: 22, height: 22,
                  display: "grid", placeItems: "center",
                  borderRadius: tk.radius.sm,
                  background: selected ? tk.color.orange : "transparent",
                  border: `1.75px solid ${selected ? tk.color.orange : "#3a3a3e"}`,
                  transition: `background ${tk.motion.fast}, border-color ${tk.motion.fast}`,
                }}>
                {selected && <Check style={{ width: 14, height: 14, color: tk.color.onOrange, strokeWidth: 3 }} />}
              </span>
            </button>
          )}

          {/* title + venue + date/time */}
          <div style={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
            <h3 style={{
              margin: 0, color: tk.color.text,
              fontFamily: tk.font.body, fontWeight: 600,
              fontSize: 20, letterSpacing: "-0.01em", lineHeight: 1.2,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%"
            }}>
              {gig.artist_name}
            </h3>
            <div style={{
              display: "flex", alignItems: "center", gap: 6, color: tk.color.textDim,
              fontFamily: tk.font.body, fontSize: 15
            }}>
              <MapPin style={{ width: 16, height: 16, flexShrink: 0 }} />
              <span style={{ minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {gig.venue_name}{gig.city ? `, ${gig.city}` : ""}
              </span>
              {gig.venue_name && gig.city && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${gig.venue_name}, ${gig.city}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isStandalonePwa()) {
                      e.preventDefault();
                      window.location.href = e.currentTarget.href;
                    }
                  }}
                  data-testid={`directions-${gig.gig_id}`}
                  title="Directions"
                  aria-label="Directions"
                  style={{
                    flexShrink: 0, width: 36, height: 36, display: "grid", placeItems: "center",
                    borderRadius: tk.radius.md,
                    background: tk.color.orangeSoft, border: `1px solid ${tk.color.orangeLine}`,
                    color: tk.color.orange, cursor: "pointer", textDecoration: "none"
                  }}
                >
                  <Navigation style={{ width: 16, height: 16 }} />
                </a>
              )}
            </div>
            <div style={{
              display: "flex", alignItems: "center", gap: 7, color: tk.color.textDim,
              fontFamily: tk.font.mono, fontSize: 13.5, letterSpacing: "0.02em", whiteSpace: "nowrap"
            }}>
              <Calendar style={{ width: 15, height: 15, flexShrink: 0 }} />
              <span>{gig.is_multiday && tripDateRange ? tripDateRange : formattedDate}</span>
              <span style={{ color: tk.color.textMuted, padding: "0 2px" }}>·</span>
              <Clock style={{ width: 15, height: 15, flexShrink: 0 }} />
              <span>{gig.start_time || "—"}</span>
            </div>
          </div>
        </div>

        <hr style={{ border: "none", borderTop: `1px solid ${tk.color.divider}`, margin: `0 0 ${tk.space.md}px` }} />

        {/* FOOTER: fee · status · payment · invoice — all direct children,
            evenly spread by space-between. With ≤3 items the fee keeps its
            indent so it lines up with the text above; with 4+ the indent drops
            to 0 so the row uses the full card width and all four fit on one
            evenly-spaced line (see footerPadLeft). */}
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: tk.space.sm, justifyContent: "space-between", paddingLeft: footerPadLeft }}>
          {/* Fee — far left */}
          {gig.fee && (
            <span style={{
              fontFamily: tk.font.mono, fontWeight: 700, fontSize: 18,
              color: tk.color.green, letterSpacing: "-0.02em", whiteSpace: "nowrap"
            }}>
              £{gig.fee}
            </span>
          )}

          {/* Status pill */}
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "5px 10px", borderRadius: tk.radius.pill,
            background: statusVisual.bg, color: statusVisual.color,
            fontFamily: tk.font.mono, fontWeight: 600, fontSize: 11,
            letterSpacing: "0.08em", textTransform: "uppercase", whiteSpace: "nowrap"
          }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: statusVisual.color }} />
            {gig.status}
          </span>

          {/* Payment pill */}
          {showPaymentPill && (
            <button
              type="button"
              onClick={handleTogglePaid}
              disabled={markingPaid}
              data-testid={`payment-status-${gig.gig_id}`}
              title={paymentVisual.label === "Paid" ? "Tap to undo — mark as pending" : "Tap to mark as paid"}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 11px", borderRadius: tk.radius.pill,
                background: paymentVisual.bg, color: paymentVisual.color, border: "none", cursor: "pointer",
                fontFamily: tk.font.mono, fontWeight: 600, fontSize: 11.5,
                letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap",
                transition: `background ${tk.motion.fast}`, opacity: markingPaid ? 0.6 : 1,
              }}
            >
              {markingPaid ? "Updating…" : paymentVisual.label}
            </button>
          )}

          {/* Invoice — push to accounting platform */}
          {freeAgentBtn}
          {xeroBtn}
          {quickbooksBtn}
        </div>
      </div>
    </div>
  );
};

// Dashboard Page
export const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [gigs, setGigs] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ upcoming: 0, total: 0, thisMonth: 0 });
  const [revenueStats, setRevenueStats] = useState(null);
  const [socialReminders, setSocialReminders] = useState([]);
  const [unpaidInvoices, setUnpaidInvoices] = useState({ count: 0, total: 0 });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalType, setUpgradeModalType] = useState(null);
  const [travelMinutes, setTravelMinutes] = useState(null);

  // Dashboard layout customization
  const [layoutPrefs, setLayoutPrefs] = useState({
    hidden_widgets: [],
    grid_order: ["upcoming_gigs", "social_schedule", "wellbeing_prompts", "revenue_overview"]
  });
  const [isSavingLayout, setIsSavingLayout] = useState(false);
  const [showWidgetManager, setShowWidgetManager] = useState(false);

  // Default layout preferences
  const DEFAULT_LAYOUT = {
    hidden_widgets: [],
    grid_order: ["upcoming_gigs", "social_schedule", "wellbeing_prompts", "revenue_overview"]
  };

  // Check if we should show upgrade modal (trial expiring or expired)
  useEffect(() => {
    if (!user?.subscription) return;
    const sub = user.subscription;
    if (sub.subscription_status === "expired") {
      setUpgradeModalType("expired");
      setShowUpgradeModal(true);
    } else if (sub.plan_type === "trial" && sub.trial_end_date) {
      const daysLeft = Math.ceil((new Date(sub.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24));
      if (daysLeft <= 2) {
        setUpgradeModalType("ending");
        setShowUpgradeModal(true);
      }
    }
  }, [user?.subscription]);

  useEffect(() => {
    fetchGigs();
    fetchRevenueStats();
    fetchCollaborators();
    fetchSocialReminders();
    fetchTravelMinutes();
    fetchUnpaidInvoices();
    fetchLayoutPreferences();
  }, []);

  // Refresh gigs when app/tab comes back into focus (catches date changes, gig_finished state, etc.)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchGigs();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // After a gig is invoiced via the one-click FA/Xero buttons on GigCard,
  // refresh the gig list so fee_status updates from "pending" → "invoiced"
  // and the buttons disappear without a page reload.
  useEffect(() => {
    const onInvoiced = () => { fetchGigs(); fetchUnpaidInvoices(); };
    window.addEventListener("fretless:gig-invoiced", onInvoiced);
    return () => window.removeEventListener("fretless:gig-invoiced", onInvoiced);
  }, []);

  const fetchUnpaidInvoices = async () => {
    // Backend Invoice.status is draft/sent/paid/overdue/cancelled — anything
    // that isn't paid or cancelled is still outstanding.
    try {
      const res = await apiClient.get("/invoices");
      const unpaid = (res.data || []).filter(inv => inv.status !== "paid" && inv.status !== "cancelled");
      const totalOutstanding = unpaid.reduce((sum, inv) => sum + (inv.total || 0), 0);
      setUnpaidInvoices({ count: unpaid.length, total: totalOutstanding });
    } catch (error) {
      console.error("Failed to fetch unpaid invoices:", error);
    }
  };

  const fetchSocialReminders = async () => {
    try {
      const response = await apiClient.get("/social/reminders");
      setSocialReminders(response.data.slice(0, 5)); // Only show top 5
    } catch (error) {
      console.error("Failed to fetch social reminders:", error);
    }
  };

  const fetchTravelMinutes = async () => {
    try {
      const res = await apiClient.get("/user/travel-settings");
      if (res.data?.home_address) {
        setTravelMinutes(45); // Default estimate; real travel time comes from GigBrief
      }
    } catch {}
  };

  const fetchRevenueStats = async () => {
    try {
      const response = await apiClient.get("/stats/revenue");
      setRevenueStats(response.data);
    } catch (error) {
      console.error("Failed to fetch revenue stats:", error);
    }
  };

  const fetchCollaborators = async () => {
    try {
      const response = await apiClient.get("/collaborators");
      setCollaborators(response.data);
    } catch (error) {
      console.error("Failed to fetch collaborators:", error);
    }
  };

  const fetchGigs = async () => {
    try {
      // Fetch only upcoming gigs for dashboard
      const response = await apiClient.get("/gigs?time_filter=upcoming");
      const upcomingGigs = response.data;
      setGigs(upcomingGigs);
      
      // Calculate stats
      const now = new Date();
      const thisMonth = upcomingGigs.filter(g => {
        if (!g.date) return false;
        const gigDate = parseISO(g.date);
        return gigDate.getMonth() === now.getMonth() && gigDate.getFullYear() === now.getFullYear();
      });
      
      // Also fetch total count for stats (all gigs)
      const allResponse = await apiClient.get("/gigs?time_filter=all");
      
      setStats({
        upcoming: upcomingGigs.length,
        total: allResponse.data.length,
        thisMonth: thisMonth.length
      });
    } catch (error) {
      console.error("Failed to fetch gigs:", error);
      // Friendly empty state for new users — never show error
      setGigs([]);
      setStats({ upcoming: 0, total: 0, thisMonth: 0 });
    } finally {
      setLoading(false);
    }
  };

  const fetchLayoutPreferences = async () => {
    try {
      const res = await apiClient.get("/user/dashboard-layout");
      if (res.data) {
        setLayoutPrefs({
          hidden_widgets: res.data.hidden_widgets || [],
          grid_order: res.data.grid_order || ["upcoming_gigs", "social_schedule", "wellbeing_prompts", "revenue_overview"]
        });
      }
    } catch (error) {
      // Use default layout if endpoint doesn't exist yet or user has no preferences
      console.debug("No custom dashboard layout found, using defaults");
    }
  };

  const saveLayoutPreferences = async (prefs) => {
    // Optimistic update: apply locally immediately so the UI doesn't flicker
    // back to the previous order while the POST is in flight.
    const previousPrefs = layoutPrefs;
    setLayoutPrefs(prefs);
    setIsSavingLayout(true);
    try {
      await apiClient.post("/user/dashboard-layout", prefs);
      return true;
    } catch (error) {
      console.error("Failed to save dashboard layout:", error);
      toast.error("Failed to save layout preferences");
      // Roll back on failure so server and client stay in sync.
      setLayoutPrefs(previousPrefs);
      return false;
    } finally {
      setIsSavingLayout(false);
    }
  };

  const handleToggleWidget = (widgetId) => {
    const newHidden = layoutPrefs.hidden_widgets.includes(widgetId)
      ? layoutPrefs.hidden_widgets.filter(id => id !== widgetId)
      : [...layoutPrefs.hidden_widgets, widgetId];

    const newPrefs = { ...layoutPrefs, hidden_widgets: newHidden };
    saveLayoutPreferences(newPrefs);
  };

  const handleReorderGrid = (newOrder) => {
    const newPrefs = { ...layoutPrefs, grid_order: newOrder };
    saveLayoutPreferences(newPrefs);
  };

  // Gigs are already filtered for upcoming, just sort and slice
  const upcomingGigs = [...gigs]
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 5);

  const nextGig = upcomingGigs[0];
  // The gig after nextGig — once a multi-day nextGig is past its own
  // performance date (i.e. we're on the return leg), the countdown badge
  // switches to this one instead of showing a negative day count relative
  // to a trip that's already happened.
  const followingGig = upcomingGigs[1];

  // Setup drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { distance: 8 }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = layoutPrefs.grid_order.indexOf(active.id);
    const newIndex = layoutPrefs.grid_order.indexOf(over.id);

    const newOrder = arrayMove(layoutPrefs.grid_order, oldIndex, newIndex);
    handleReorderGrid(newOrder);
  };

  // Widget visibility check helper
  const isWidgetHidden = (widgetId) => layoutPrefs.hidden_widgets.includes(widgetId);

  // Reset dashboard to default layout
  const handleResetLayout = async () => {
    if (!confirm("Reset dashboard to default layout? This will show all widgets in the default order.")) {
      return;
    }

    const ok = await saveLayoutPreferences(DEFAULT_LAYOUT);
    if (ok) {
      toast.success("Dashboard reset to default layout");
      setShowWidgetManager(false);
    }
  };

  // All available widgets with metadata
  const AVAILABLE_WIDGETS = [
    { id: "gig_brief", label: "Next Gig Brief", category: "banner" },
    { id: "trial_banner", label: "Trial/Subscription Banner", category: "banner" },
    { id: "upcoming_gigs", label: "Upcoming Gigs", category: "grid" },
    { id: "social_schedule", label: "Weekly Social Schedule", category: "grid" },
    { id: "wellbeing_prompts", label: "Wellbeing Prompts", category: "grid" },
    { id: "revenue_overview", label: "Revenue Overview", category: "grid" }
  ];

  return (
    <Layout>
      <div data-testid="dashboard-page">
        {/* Header — DS tokens (kicker + type.heroSm). The widget grid
            below uses the same Kicker primitive for widget titles, so
            the page-intro and the widget headers share one type system. */}
        <div
          style={{
            marginBottom: tokens.space.xxl,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: tokens.space.md,
          }}
        >
          <div>
            <Kicker color={tokens.color.orange} style={{ marginBottom: tokens.space.xs }}>
              Dashboard
            </Kicker>
            <h1
              style={{
                margin: 0,
                ...tokens.type.heroSm,
                color: tokens.color.text,
                textWrap: "balance",
              }}
            >
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
          </div>
          {/* Header actions: Share profile + Customize */}
          <div className="flex items-center gap-1">
          <ShareProfilePopover />
          {/* Customize Dashboard Button */}
          <Popover open={showWidgetManager} onOpenChange={setShowWidgetManager}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="gap-2"
                title="Customize dashboard widgets"
              >
                <Settings className="h-4 w-4" />
                <span className="hidden sm:inline text-sm">Customize</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4" align="end">
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-sm mb-3">Manage Widgets</h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {AVAILABLE_WIDGETS.map((widget) => (
                      <label key={widget.id} className="flex items-center gap-2 cursor-pointer hover:bg-secondary/50 p-2 rounded transition-colors">
                        <input
                          type="checkbox"
                          checked={!isWidgetHidden(widget.id)}
                          onChange={(e) => {
                            if (e.target.checked && isWidgetHidden(widget.id)) {
                              // Show widget
                              handleToggleWidget(widget.id);
                            } else if (!e.target.checked && !isWidgetHidden(widget.id)) {
                              // Hide widget
                              handleToggleWidget(widget.id);
                            }
                          }}
                          className="w-4 h-4 rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{widget.label}</p>
                          <p className="text-xs text-muted-foreground">{widget.category}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="border-t pt-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Appearance</p>
                    <p className="text-xs text-muted-foreground">Light or dark theme</p>
                  </div>
                  <ThemeToggle />
                </div>
                <div className="border-t pt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResetLayout}
                    className="w-full text-xs"
                  >
                    Reset to Default Layout
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          </div>
        </div>

        {/* Next Gig Brief with Countdown */}
        {nextGig && !loading && !isWidgetHidden("gig_brief") && (
          <div className="mb-6" data-testid="gig-brief-section">
            <GigBrief
              gig={nextGig}
              followingGig={followingGig}
              collaborators={collaborators}
              travelMinutes={travelMinutes}
              onHide={() => handleToggleWidget("gig_brief")}
            />
          </div>
        )}

        {/* Subscription Banner */}
        {!isWidgetHidden("trial_banner") && (
          <div className="relative group mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleToggleWidget("trial_banner")}
              className="absolute top-2 right-2 h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity z-10"
              title="Hide widget"
            >
              <X className="h-4 w-4" />
            </Button>
            <TrialBanner subscription={user?.subscription} />
          </div>
        )}


        {/* ── Main Layout ──
            Desktop: Upcoming Gigs + Social Schedule side-by-side, then full-width
                     Revenue Overview below. All draggable & customizable.
            Mobile:  Single-column stack — Upcoming Gigs → Social Schedule → Revenue.
            Gig Brief sits above this whole block. Users can hide/reorder any widget. */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 auto-rows-max">
            <SortableContext items={layoutPrefs.grid_order} strategy={rectSortingStrategy}>
              {layoutPrefs.grid_order.map((widgetId) => {
                // Skip if widget is hidden
                if (isWidgetHidden(widgetId)) return null;

                // Render widget based on ID
                switch (widgetId) {
                  case "upcoming_gigs":
                    return (
                      <SortableGridItem key="upcoming_gigs" id="upcoming_gigs">
                        <UpcomingGigs
                          gigs={upcomingGigs}
                          loading={loading}
                          navigate={navigate}
                          onHide={() => handleToggleWidget("upcoming_gigs")}
                          SharePopover={SharePopover}
                        />
                      </SortableGridItem>
                    );

                  case "social_schedule":
                    return (
                      <SortableGridItem key="social_schedule" id="social_schedule">
                        <WeeklySocialSchedule
                          gigs={gigs}
                          navigate={navigate}
                          onHide={() => handleToggleWidget("social_schedule")}
                        />
                      </SortableGridItem>
                    );

                  case "wellbeing_prompts":
                    return (
                      <SortableGridItem key="wellbeing_prompts" id="wellbeing_prompts">
                        <WellbeingPrompts
                          onHide={() => handleToggleWidget("wellbeing_prompts")}
                        />
                      </SortableGridItem>
                    );

                  case "revenue_overview":
                    return (
                      <SortableGridItem key="revenue_overview" id="revenue_overview">
                        <RevenueOverview
                          revenueStats={revenueStats}
                          unpaidInvoices={unpaidInvoices}
                          navigate={navigate}
                          onHide={() => handleToggleWidget("revenue_overview")}
                        />
                      </SortableGridItem>
                    );

                  default:
                    return null;
                }
              })}
            </SortableContext>
          </div>
        </DndContext>
      </div>
      <UpgradeModal 
        show={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
        type={upgradeModalType} 
      />
    </Layout>
  );
};

export default Dashboard;
